import styled from '@emotion/styled'
import {
    ApiShopperLandingPageLink,
    BrandChatComparison as Comparison,
} from '@storefronts/common-types'
import { useContext } from 'react'
import {
    BUTTON_BORDER_RADIUS_VAR,
    MEDIA_BORDER_RADIUS_VAR,
    SUBTLE_BORDER_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { PageContext } from '../../context/PageContextProvider'
import { AnalyticsContext } from '../../context/analyticsContext'
import { ViddyLPContext } from '../../context/viddyLPContext'
import { BRAND_CHAT_FONT } from './brandChatStyles'
import { imageUrlTransform } from '../../utils/imageUrlTransform'
import { getLinkPrices, priceFormat } from '../../utils/priceFormat'

const LABEL_COLUMN_WIDTH = 56

export const BrandChatComparison: React.FC<{
    comparison: Comparison
    links: Map<string, ApiShopperLandingPageLink>
}> = ({ comparison, links }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { setActiveLPLink } = useContext(PageContext)
    const { Analytics } = useContext(AnalyticsContext)
    const openProduct = (link: ApiShopperLandingPageLink) => {
        Analytics.TRACK_BRAND_CHAT_PRODUCT_CLICK(link, 'comparison')
        setActiveLPLink(link)
    }

    const columns = comparison.productIds.flatMap((productId) => {
        const link = links.get(productId)
        return link ? [link] : []
    })
    if (columns.length < 2) return null

    const currencySymbol = landingPage.client.currencySymbol
    const discount = landingPage.discount ?? null

    return (
        <Table
            style={{
                gridTemplateColumns: `${LABEL_COLUMN_WIDTH}px repeat(${columns.length}, minmax(0, 1fr))`,
            }}
        >
            <span />
            {columns.map((link) => (
                <Product
                    key={link.linkHash}
                    type="button"
                    onClick={() => openProduct(link)}
                >
                    <img src={imageUrlTransform(link.imageUrl)} alt="" />
                    <ProductTitle>{link.title}</ProductTitle>
                </Product>
            ))}
            <Label>Price</Label>
            {columns.map((link) => (
                <Value key={link.linkHash}>
                    {priceFormat(
                        getLinkPrices(link, discount).price,
                        currencySymbol,
                    )}
                </Value>
            ))}
            <Label>Rating</Label>
            {columns.map((link) => (
                <Value key={link.linkHash}>
                    {link.reviews?.count && link.reviews.rating != null
                        ? `${link.reviews.rating.toFixed(1)} (${link.reviews.count})`
                        : '—'}
                </Value>
            ))}
            {comparison.rows.map((row) => (
                <Row key={row.label}>
                    <Label>{row.label}</Label>
                    {columns.map((link) => (
                        <Value key={link.linkHash}>
                            {row.valuesByProductId[link.shopifyId] ?? '—'}
                        </Value>
                    ))}
                </Row>
            ))}
            <span />
            {columns.map((link) => (
                <ViewButton
                    key={link.linkHash}
                    type="button"
                    onClick={() => openProduct(link)}
                >
                    View
                </ViewButton>
            ))}
        </Table>
    )
}

const Table = styled.div`
    display: grid;
    gap: 8px;
    padding: 10px;
    border: 1px solid var(${SUBTLE_BORDER_COLOR_VAR}, #e5e5e5);
    border-radius: 16px;
    background: #ffffff;
    font-size: 12px;
    align-items: start;
    color: #0d0d0d;
`

const Row = styled.div`
    display: contents;
`

const Product = styled.button`
    appearance: none;
    border: 0;
    padding: 0;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;

    img {
        width: 100%;
        aspect-ratio: 1;
        object-fit: cover;
        border-radius: var(${MEDIA_BORDER_RADIUS_VAR}, 10px);
        background: #f4f4f4;
    }
`

const ProductTitle = styled.span`
    font-family: ${BRAND_CHAT_FONT};
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`

const ViewButton = styled.button`
    appearance: none;
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 0;
    border-radius: var(${BUTTON_BORDER_RADIUS_VAR}, 999px);
    border: 1px solid currentColor;
    background: transparent;
    color: inherit;
    cursor: pointer;
`

const Label = styled.span`
    color: #6e6e73;
`

const Value = styled.span`
    line-height: 1.35;
`
